import { useMutation, useQueryClient } from "@tanstack/react-query";

import { type TraineeSettings } from "@fitness-app/data-models/entities/TraineeSettings";

import { traineeKeys, useTraineeId } from "~/modules/Trainee/TraineeProfile/scope";
import { analyticsService, supabase } from "~/store/initializeStore";

type SettingsSection = Pick<TraineeSettings, "notificationsSettings" | "workoutSettings">;

const ANALYTICS_EVENTS: Record<keyof SettingsSection, string> = {
  notificationsSettings: "update_trainee_notifications_settings",
  workoutSettings: "update_trainee_workout_settings",
};

/**
 * Zapis sekcji ustawień klientki (to samo co thunki `updateTraineeNotificationsSettings`
 * i `updateTraineeWorkoutSettings`), bez pisania do `store.trainee`, który należy do buildera.
 */
export const useUpdateTraineeSettings = <K extends keyof SettingsSection>(section: K) => {
  const traineeId = useTraineeId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (value: SettingsSection[K]) => {
      const update = { [section]: value } as Partial<SettingsSection>;
      const { data, error } = await supabase
        .from("trainee_settings")
        .update({ ...update, updatedAt: new Date().toISOString() })
        .eq("id", traineeId)
        .select("*")
        .single<TraineeSettings>();

      if (error || !data) {
        throw new Error(error?.message || "Failed to update notifications settings");
      }

      analyticsService.track(ANALYTICS_EVENTS[section]);

      return data;
    },
    onSuccess: (settings) => {
      queryClient.setQueryData(traineeKeys.settings(traineeId), settings);
    },
  });
};
